// src/components/exam-builder/FileUploadModal.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  allowed: "pdf,docx,png,jpg",
  maxSizeMB: 25,
  points: 5,
  status: "published",
  tags: "",
  layout: "top-bottom",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem = item.stem || {};
  const body = item.body || {};

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa: stem.fa || "",
    stem_en: stem.en || "",
    allowed: Array.isArray(body.allowed)
      ? body.allowed.join(",")
      : body.allowed || "pdf,docx,png,jpg",
    maxSizeMB:
      typeof body.maxSizeMB === "number" && !Number.isNaN(body.maxSizeMB)
        ? body.maxSizeMB
        : 25,
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 5,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : item.tags || "",
    layout: body.layout || "top-bottom",
    explanation_fa,
    explanation_en,
  };
};

export default function FileUploadModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("file_upload.toasts.stem_required"));
      return;
    }

    const exts = (form.allowed || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    if (exts.length === 0) {
      toast.error(t("file_upload.toasts.need_ext"));
      return;
    }

    const payload = {
      kind: "file_upload",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        allowed: exts,
        maxSizeMB: Number(form.maxSizeMB) || 10,
        layout: form.layout,
      },
      points: Number(form.points) || 0,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("file_upload.toasts.updated", "آیتم آپلود فایل به‌روزرسانی شد")
          : t("file_upload.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("file_upload.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("file_upload.stem_en_ph")}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("select_sentence.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`select_sentence.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Allowed / maxSize / points */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.allowed")}
          </label>
          <input
            name="allowed"
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("file_upload.allowed_ph")}
            value={form.allowed}
            onChange={onChange}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.max_size")}
          </label>
          <input
            type="number"
            name="maxSizeMB"
            min={1}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("file_upload.max_size_ph")}
            value={form.maxSizeMB}
            onChange={onChange}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.points")}
          </label>
          <input
            type="number"
            name="points"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={onChange}
          />
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            name="status"
            onChange={onChange}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("file_upload.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            name="tags"
            onChange={onChange}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
